import { memo, type ReactNode } from 'react';
import { Handle, Position, type Node, type NodeProps } from '@xyflow/react';
import clsx from 'clsx';
import { AlertTriangle, Bell, CirclePlay, Flag, GitBranch, Layers, ListChecks, Merge } from '@/components/icons';
import type { FlowNodeType } from '@1qa/shared';
import { notifyLabel, type StepData } from '@/lib/flowView';

const ICONS: Record<FlowNodeType, typeof Flag> = {
  start: CirclePlay,
  test: ListChecks,
  group: Layers,
  condition: GitBranch,
  join: Merge,
  notify: Bell,
  end: Flag,
};

/** `ring` outlines the box shapes (CSS), `stroke` the drawn ones (SVG: the Condition diamond, the Notify display and the End octagon). */
const STATUS: Record<string, { label: string; ring: string; stroke: string; text: string }> = {
  PENDING: { label: 'Waiting', ring: 'ring-1 ring-slate-200 dark:ring-slate-700', stroke: 'stroke-slate-200 dark:stroke-slate-700', text: 'text-slate-500' },
  RUNNING: { label: 'Running', ring: 'ring-2 ring-brand-500', stroke: 'stroke-brand-500 [stroke-width:2]', text: 'text-brand-600 dark:text-brand-300' },
  PASSED: { label: 'Passed', ring: 'ring-2 ring-emerald-500', stroke: 'stroke-emerald-500 [stroke-width:2]', text: 'text-emerald-700 dark:text-emerald-300' },
  FAILED: { label: 'Failed', ring: 'ring-2 ring-rose-500', stroke: 'stroke-rose-500 [stroke-width:2]', text: 'text-rose-700 dark:text-rose-300' },
  ERROR: { label: 'Error', ring: 'ring-2 ring-amber-500', stroke: 'stroke-amber-500 [stroke-width:2]', text: 'text-amber-700 dark:text-amber-300' },
  CANCELLED: { label: 'Cancelled', ring: 'ring-1 ring-slate-300 dark:ring-slate-600', stroke: 'stroke-slate-300 dark:stroke-slate-600', text: 'text-slate-500' },
  SKIPPED: { label: 'Not run', ring: 'ring-1 ring-dashed ring-slate-300 dark:ring-slate-600', stroke: 'stroke-slate-300 [stroke-dasharray:4_3] dark:stroke-slate-600', text: 'text-slate-400' },
};
const IDLE = { ring: 'ring-1 ring-slate-200 dark:ring-slate-700', stroke: 'stroke-slate-300 dark:stroke-slate-600' };
const PROBLEM = { ring: 'ring-2 ring-amber-400', stroke: 'stroke-amber-400 [stroke-width:2]' };

/** Outlines of the drawn shapes, in a 100 x 100 box stretched over the step. */
const DIAMOND = 'M50 1 L99 50 L50 99 L1 50 Z';
const OCTAGON = 'M14 1 L86 1 L99 26 L99 74 L86 99 L14 99 L1 74 L1 26 Z';
/** The flowchart "display" symbol: pointed on the left, rounded on the right. */
const DISPLAY = 'M12 1 L88 1 C96 1 99 25 99 50 C99 75 96 99 88 99 L12 99 L1 50 Z';

export type StepNodeType = Node<StepData, 'step'>;

const handleClass = '!size-3 !border-2 !border-white !bg-slate-400 dark:!border-slate-900';

/** A drawn outline (SVG) behind a step's content; `vector-effect` keeps the line even though the box is stretched. */
function Outline({ d, stroke }: { d: string; stroke: string }) {
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 size-full overflow-visible drop-shadow-sm" aria-hidden="true">
      <path d={d} vectorEffect="non-scaling-stroke" className={clsx('fill-white dark:fill-slate-900', stroke)} />
    </svg>
  );
}

/**
 * One step of a flow on the canvas, shaped like the matching flowchart symbol so each kind is easy to spot:
 * Start a pill (terminator), Test a box (process), Group a stack of boxes, Condition a diamond (decision) with Yes
 * out of the left corner and No out of the right, Join a circle (connector), Notify the display symbol (a message
 * goes out) and End an octagon (stop). Every step
 * keeps the same 224 px width so saved diagrams stay lined up. In the run view it also shows its status.
 */
export const StepNode = memo(function StepNode({ data, selected }: NodeProps<StepNodeType>) {
  const { node, title, detail, problem, run } = data;
  const Icon = ICONS[node.type];
  const status = run ? (STATUS[run.status] ?? STATUS.PENDING!) : null;
  const tone = status ?? (problem ? PROBLEM : IDLE);
  const statusLine = status && node.type !== 'start' && (
    <div className={clsx('flex items-center justify-between gap-2 text-xs font-medium', status.text)}>
      <span className={clsx(run?.status === 'RUNNING' && 'motion-safe:animate-pulse')}>
        {node.type === 'condition' && run?.outcome ? `Answer: ${run.outcome === 'yes' ? 'Yes' : 'No'}` : node.type === 'notify' ? notifyLabel(run) ?? status.label : status.label}
      </span>
      {run?.stats && run.stats.total > 0 && (node.type === 'test' || node.type === 'group') && (
        <span className="text-slate-500">
          {run.stats.passed}/{run.stats.total} passed
        </span>
      )}
    </div>
  );
  const warning = problem && !run && <AlertTriangle className="size-4 shrink-0 text-amber-500" aria-label="Needs attention" />;
  const text = (
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">{title}</div>
      <div className="truncate text-xs text-slate-500 dark:text-slate-400" title={detail}>
        {detail}
      </div>
    </div>
  );
  const chip = (bg: string, round = 'rounded-lg') => (
    <span className={clsx('grid size-8 shrink-0 place-items-center text-white', round, bg)}>
      <Icon className="size-4" aria-hidden="true" />
    </span>
  );
  const frame = (body: ReactNode, className?: string) => (
    <div
      className={clsx(
        'relative w-56',
        selected && 'outline-2 outline-offset-4 outline-brand-500',
        (node.type === 'start' || node.type === 'test' || node.type === 'group') && 'rounded-md',
        run?.status === 'SKIPPED' && 'opacity-60',
        className,
      )}
    >
      {node.type !== 'start' && <Handle type="target" position={Position.Top} className={handleClass} />}
      {body}
      {node.type === 'condition' ? (
        <>
          <Handle id="yes" type="source" position={Position.Left} className={clsx(handleClass, '!bg-emerald-500')} />
          <Handle id="no" type="source" position={Position.Right} className={clsx(handleClass, '!bg-rose-500')} />
        </>
      ) : (
        node.type !== 'end' && <Handle type="source" position={Position.Bottom} className={handleClass} />
      )}
    </div>
  );

  switch (node.type) {
    case 'condition':
      return frame(
        <>
          <Outline d={DIAMOND} stroke={tone.stroke} />
          <div className="relative mx-auto flex h-full w-32 flex-col items-center justify-center gap-0.5 text-center">
            <div className="flex max-w-full items-center gap-1">
              <Icon className="size-3.5 shrink-0 text-amber-500" aria-hidden="true" />
              <span className="truncate text-sm font-semibold text-slate-900 dark:text-white">{title}</span>
              {warning}
            </div>
            <div className="line-clamp-2 text-[11px] leading-tight text-slate-500 dark:text-slate-400" title={detail}>
              {detail}
            </div>
            {statusLine}
          </div>
          <span className="absolute top-[calc(50%-1.4rem)] left-0 text-[10px] font-semibold tracking-wide text-emerald-600 uppercase dark:text-emerald-400">Yes</span>
          <span className="absolute top-[calc(50%-1.4rem)] right-0 text-[10px] font-semibold tracking-wide text-rose-600 uppercase dark:text-rose-400">No</span>
        </>,
        'h-32',
      );
    case 'notify':
      return frame(
        <>
          <Outline d={DISPLAY} stroke={tone.stroke} />
          <div className="relative space-y-1 py-2.5 pr-4 pl-7">
            <div className="flex items-center gap-2.5">
              {chip('bg-violet-500')}
              {text}
              {warning}
            </div>
            {statusLine}
          </div>
        </>,
      );
    case 'end':
      return frame(
        <>
          <Outline d={OCTAGON} stroke={tone.stroke} />
          <div className="relative space-y-1 px-5 py-2.5">
            <div className="flex items-center gap-2.5">
              {chip('bg-slate-700 dark:bg-slate-600', 'rounded-md')}
              {text}
              {warning}
            </div>
            {statusLine}
          </div>
        </>,
      );
    case 'join':
      // A connector circle in the middle of the step's width (where the lines meet), its text beside it.
      return frame(
        <>
          <span className={clsx('mx-auto grid size-14 place-items-center rounded-full bg-sky-500 text-white shadow-soft ring-offset-2 ring-offset-slate-50 dark:ring-offset-slate-950', status || problem ? tone.ring : 'ring-0')}>
            <Icon className="size-5" aria-hidden="true" />
          </span>
          <div className="absolute top-1/2 left-[calc(50%+2.25rem)] w-[calc(50%-2.25rem)] -translate-y-1/2 space-y-0.5">
            <div className="flex items-center gap-1">
              {text}
              {warning}
            </div>
            {statusLine}
          </div>
        </>,
      );
    default: {
      const pill = node.type === 'start';
      const box = (
        <div className={clsx('relative space-y-1.5 bg-white px-3 py-2.5 shadow-soft dark:bg-slate-900', pill ? 'rounded-full px-4' : 'rounded-md', tone.ring)}>
          <div className="flex items-center gap-2.5">
            {chip(pill ? 'bg-emerald-600' : 'bg-brand-gradient', pill ? 'rounded-full' : 'rounded-lg')}
            {text}
            {warning}
          </div>
          {statusLine}
        </div>
      );
      return frame(
        node.type === 'group' ? (
          // A stack of boxes: a group is several tests.
          <>
            <div className={clsx('absolute inset-0 translate-x-2 -translate-y-2 rounded-md bg-white dark:bg-slate-900', IDLE.ring)} aria-hidden="true" />
            <div className={clsx('absolute inset-0 translate-x-1 -translate-y-1 rounded-md bg-white dark:bg-slate-900', IDLE.ring)} aria-hidden="true" />
            {box}
          </>
        ) : (
          box
        ),
      );
    }
  }
});

/** A small picture of a step's shape, for the "Add" buttons, so they match what lands on the canvas. */
export function StepShapeIcon({ type, className }: { type: FlowNodeType; className?: string }) {
  const line = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinejoin: 'round' as const };
  return (
    <svg viewBox="0 0 24 16" className={clsx('h-4 w-6', className)} aria-hidden="true">
      {type === 'start' && <rect x="1" y="3" width="22" height="10" rx="5" {...line} />}
      {type === 'test' && <rect x="2" y="3" width="20" height="10" rx="1.5" {...line} />}
      {type === 'group' && (
        <>
          <path d="M6 1.5h15.5v8" {...line} />
          <path d="M4 3.5h15.5v8" {...line} />
          <rect x="2" y="5.5" width="15.5" height="9" rx="1.5" {...line} />
        </>
      )}
      {type === 'condition' && <polygon points="12,1 23,8 12,15 1,8" {...line} />}
      {type === 'join' && <circle cx="12" cy="8" r="6.5" {...line} />}
      {type === 'notify' && <path d="M5 2h13c3 0 5 2.5 5 6s-2 6-5 6H5L1 8z" {...line} />}
      {type === 'end' && <polygon points="5,2 19,2 23,6 23,10 19,14 5,14 1,10 1,6" {...line} />}
    </svg>
  );
}
